import * as Constants from 'const';
import { TextAbbreviationJSON } from 'models';
import { CellEntityType, ParagraphType, SegmentType } from 'services/ArtboardConverter/constants';
import { Schemas } from 'services/ArtboardConverter/models';
import { toPx } from 'utils/toPx';
import { abbreviationsListToHTML } from '../utils/abbreviationsListToHTML';

enum NodeName {
  TEXT = '#text',
  SPAN = 'SPAN',
  SUP = 'SUP',
  SUB = 'SUB',
}

type AbbrPart = { text: string; isSuperScript: boolean; isSubScript: boolean };

export function convertAbbreviationsListToParts(
  list: TextAbbreviationJSON[],
): AbbrPart[] {
  const parts: AbbrPart[] = [];
  let currentPart: AbbrPart | undefined;
  const catchPart = (node: Node): void => {
    const { nodeName, textContent } = node;
    if (!([NodeName.TEXT, NodeName.SUP, NodeName.SUB] as string[]).includes(nodeName)) {
      throw new Error(`Wrong node: ${nodeName}`);
    }
    const text = textContent ?? '';
    const isSuperScript = nodeName === NodeName.SUP;
    const isSubScript = nodeName === NodeName.SUB;
    if (currentPart?.isSuperScript === isSuperScript && currentPart?.isSubScript === isSubScript) {
      currentPart.text += text;

      return;
    }
    if (currentPart) {
      parts.push(currentPart);
    }
    currentPart = { text, isSuperScript, isSubScript };
  };

  const html = abbreviationsListToHTML(list);
  const parser = new DOMParser();
  const dom = parser.parseFromString(html, 'text/html');
  dom.body.childNodes.forEach((node) => {
    if (node.nodeName === NodeName.SPAN) {
      return node.childNodes.forEach(child => catchPart(child));
    }
    catchPart(node);
  });

  if (currentPart) {
    parts.push(currentPart);
  }

  return parts;
}

type Styles = {
  color: string;
  fontFamily: string;
  fontSize: number;
  lineHeight: number;
  align: string;
  valign: string;
};

export function abbreviationsListToSchema(
  list: TextAbbreviationJSON[],
  styles: Styles,
): Schemas.Text {
  const { color, fontFamily, fontSize, lineHeight, align, valign } = styles;

  const parts = convertAbbreviationsListToParts(list).map((item): Schemas.Part => {
    const { text, isSuperScript, isSubScript } = item;
    const partFontSize = isSuperScript ? fontSize * Constants.SCRIPT_BASIC_FONT_REDUCTION : fontSize;

    return {
      sup: isSuperScript,
      sub: isSubScript,
      bold: false,
      italic: false,
      underline: false,
      fontFamily,
      fontSize: toPx(partFontSize, 2),
      color,
      text,
      lineHeight: lineHeight as unknown as string, // to match current behaviour nubmer should be provided insted of string
      lineHeightPx: toPx(Math.floor(partFontSize) * lineHeight, 2),
      bulletColor: '',
    };
  });

  const paragraph: Schemas.TextParagraph & { valign: string } = {
    type: ParagraphType.PLAINT_TEXT,
    align,
    lineHeight: lineHeight.toString(),
    fontFamily,
    fontSize: toPx(fontSize, 2),
    color,
    anchor: false,
    valign,
    segments: [{ type: SegmentType.TEXT, parts }],
  };

  return {
    entityType: CellEntityType.TEXT,
    paragraphs: [paragraph],
  };
}
